import {
    Activity,
    Building2,
    ClipboardCheck,
    ClipboardList,
    FileQuestion,
    FileSearch,
    FileSignature,
    FileType,
    FolderTree,
    Gem,
    GitBranch,
    HelpCircle,
    Layers,
    LayoutGrid,
    Library,
    Link2,
    ListChecks,
    ListTree,
    Lock,
    Mail,
    Network,
    Scale,
    Target,
    Timer,
    Users,
    BarChart3,
    Grid3x3,
    Zap,
    GraduationCap,
    BookOpen,
    Globe,
    Globe2,
    Trash2,
    Database,
    KeyRound,
} from 'lucide-react';

import { DEFINITIONS_BASE_PATH } from '../../config/definitions-base-path.js';
import { complianceTabsConfig } from '../../config/complianceTabsConfig.js';
import { documentTabsConfig } from '../../config/documentTabsConfig.js';
import { generalSystemTabsConfig } from '../../config/generalSystemTabsConfig.js';
import { riskAnalysisTabsConfig } from '../../config/riskAnalysisTabsConfig.js';
import { platformHubTabsConfig } from '../../config/platformHubTabsConfig.js';
import { isoTrainingTabsConfig } from '../../config/isoTrainingTabsConfig.js';

/** @typedef {{ id: string, to: string, label: string, description?: string, pageKey?: string, icon?: import('react').ComponentType<{ className?: string }>, end?: boolean }} ReferenceNavItem */
/** @typedef {{ id: string, label: string, items: ReferenceNavItem[] }} ReferenceNavGroup */

/**
 * @param {ReferenceNavGroup[] | undefined} groups
 * @returns {ReferenceNavItem[]}
 */
export function flattenNavGroups(groups) {
    if (!Array.isArray(groups)) {
        return [];
    }
    return groups.flatMap((g) => g.items || []);
}

/**
 * Flatten reference-nav groups into main-sidebar leaf items.
 * @param {ReferenceNavGroup[] | undefined} groups
 * @returns {Array<{ id: string, title: string, url: string, pageKey?: string, exact?: boolean }>}
 */
export function mapReferenceGroupsToSidebarItems(groups) {
    return flattenNavGroups(groups).map((item) => ({
        id: item.id,
        title: item.label,
        url: item.to,
        ...(item.pageKey ? { pageKey: item.pageKey } : {}),
        ...(item.end ? { exact: true } : {}),
    }));
}

/**
 * Unified personal-data definitions sidebar (KVKK + privacy master + consent + DSAR defs).
 * One catalog everywhere — no duplicate module jumpers (e.g. "KVKK referans" ≈ company,
 * "ROPA/saklama" ≈ retention periods).
 */
const PERSONAL_DATA_GROUP_ORDER = [
    'organizationData',
    'processingAndLegal',
    'transfer',
    'retentionAndDpia',
    'explicitConsent',
    'applicationTracking',
    'measures',
];

/** @type {Array<{ id: string, group: string, to: string, pageKey: string, labelKey: string, descriptionKey: string, icon: import('react').ComponentType<{ className?: string }>, color: string, end?: boolean }>} */
const PERSONAL_DATA_NAV_CATALOG = [
    // Kurum ve veri envanteri
    {
        id: 'company',
        group: 'organizationData',
        to: `${DEFINITIONS_BASE_PATH}/kvkk/company`,
        pageKey: 'definitionsKvkkCompany',
        labelKey: 'itemLabels.kvkk.company',
        descriptionKey: 'kvkk.company',
        icon: Building2,
        color: '#3b82f6',
    },
    {
        id: 'category',
        group: 'organizationData',
        to: `${DEFINITIONS_BASE_PATH}/kvkk/category`,
        pageKey: 'definitionsKvkkCategory',
        labelKey: 'itemLabels.kvkk.category',
        descriptionKey: 'kvkk.category',
        icon: Layers,
        color: '#8b5cf6',
    },
    {
        id: 'subject-person-group',
        group: 'organizationData',
        to: `${DEFINITIONS_BASE_PATH}/kvkk/subject-person-group`,
        pageKey: 'definitionsKvkkSubjectPersonGroup',
        labelKey: 'itemLabels.kvkk.subjectPersonGroup',
        descriptionKey: 'kvkk.subjectPersonGroup',
        icon: Users,
        color: '#10b981',
    },
    {
        id: 'data-source',
        group: 'organizationData',
        to: `${DEFINITIONS_BASE_PATH}/kvkk/data-source`,
        pageKey: 'kvkkDataSource',
        labelKey: 'itemLabels.kvkk.dataSource',
        descriptionKey: 'kvkk.dataSource',
        icon: Database,
        color: '#65a30d',
    },
    // İşleme ve hukuki dayanak
    {
        id: 'processing-purpose',
        group: 'processingAndLegal',
        to: `${DEFINITIONS_BASE_PATH}/kvkk/processing-purpose`,
        pageKey: 'definitionsKvkkProcessingPurpose',
        labelKey: 'itemLabels.kvkk.processingPurpose',
        descriptionKey: 'kvkk.processingPurpose',
        icon: Target,
        color: '#06b6d4',
    },
    {
        id: 'legal-reason',
        group: 'processingAndLegal',
        to: `${DEFINITIONS_BASE_PATH}/kvkk/legal-reason`,
        pageKey: 'definitionsKvkkLegalReason',
        labelKey: 'itemLabels.kvkk.legalReason',
        descriptionKey: 'kvkk.legalReason',
        icon: Scale,
        color: '#64748b',
    },
    {
        id: 'privacy-principle',
        group: 'processingAndLegal',
        to: `${DEFINITIONS_BASE_PATH}/kvkk/privacy-principle`,
        pageKey: 'kvkkPrivacyPrinciple',
        labelKey: 'itemLabels.kvkk.privacyPrinciple',
        descriptionKey: 'kvkk.privacyPrinciple',
        icon: BookOpen,
        color: '#7c3aed',
    },
    {
        id: 'collection-method',
        group: 'processingAndLegal',
        to: `${DEFINITIONS_BASE_PATH}/kvkk/collection-method`,
        pageKey: 'definitionsKvkkCollectionMethod',
        labelKey: 'itemLabels.kvkk.collectionMethod',
        descriptionKey: 'kvkk.collectionMethod',
        icon: FolderTree,
        color: '#f59e0b',
    },
    // Aktarım
    {
        id: 'recipient-group',
        group: 'transfer',
        to: `${DEFINITIONS_BASE_PATH}/kvkk/recipient-group`,
        pageKey: 'kvkkRecipientGroup',
        labelKey: 'itemLabels.kvkk.recipientGroup',
        descriptionKey: 'kvkk.recipientGroup',
        icon: Users,
        color: '#0ea5e9',
    },
    {
        id: 'transfer-type',
        group: 'transfer',
        to: `${DEFINITIONS_BASE_PATH}/kvkk/international-transfer/transfer-types`,
        pageKey: 'definitionsKvkkTransferType',
        labelKey: 'itemLabels.kvkk.transferType',
        descriptionKey: 'kvkk.transferType',
        icon: Globe2,
        color: '#6366f1',
    },
    {
        id: 'international-transfer',
        group: 'transfer',
        to: `${DEFINITIONS_BASE_PATH}/kvkk/international-transfer`,
        pageKey: 'definitionsKvkkInternationalTransfer',
        labelKey: 'itemLabels.kvkk.internationalTransferMechanisms',
        descriptionKey: 'kvkk.internationalTransferMechanisms',
        icon: Globe,
        color: '#4f46e5',
        end: true,
    },
    // Saklama, imha ve DPIA
    {
        id: 'retention-periods',
        group: 'retentionAndDpia',
        to: `${DEFINITIONS_BASE_PATH}/privacy/retention-periods`,
        pageKey: 'kvkkRetentionPeriod',
        labelKey: 'privacyTabs.retention-periods',
        descriptionKey: 'privacyMasterNav.retention-periods',
        icon: Timer,
        color: '#f59e0b',
    },
    {
        id: 'data-disposal-method',
        group: 'retentionAndDpia',
        to: `${DEFINITIONS_BASE_PATH}/kvkk/data-disposal-method`,
        pageKey: 'kvkkDataDisposalMethod',
        labelKey: 'itemLabels.kvkk.dataDisposalMethod',
        descriptionKey: 'kvkk.dataDisposalMethod',
        icon: Trash2,
        color: '#dc2626',
    },
    {
        id: 'dpia-templates',
        group: 'retentionAndDpia',
        to: `${DEFINITIONS_BASE_PATH}/privacy/dpia-templates`,
        pageKey: 'definitionsPrivacyDpiaTemplates',
        labelKey: 'privacyTabs.dpia-templates',
        descriptionKey: 'privacyMasterNav.dpia-templates',
        icon: FileSearch,
        color: '#f43f5e',
    },
    // Açık rıza
    {
        id: 'explicit-consent-method',
        group: 'explicitConsent',
        to: `${DEFINITIONS_BASE_PATH}/obtaining-explicit-consent/method`,
        pageKey: 'definitionsObtainingExplicitConsentMethod',
        labelKey: 'itemLabels.explicitConsent.explicitConsentMethod',
        descriptionKey: 'explicitConsent.explicitConsentMethod',
        icon: FileSignature,
        color: '#8b5cf6',
    },
    {
        id: 'explicit-consent-reason',
        group: 'explicitConsent',
        to: `${DEFINITIONS_BASE_PATH}/obtaining-explicit-consent/reason`,
        pageKey: 'definitionsObtainingExplicitConsentReason',
        labelKey: 'itemLabels.explicitConsent.explicitConsentReason',
        descriptionKey: 'explicitConsent.explicitConsentReason',
        icon: HelpCircle,
        color: '#06b6d4',
    },
    // Başvuru tanımları
    {
        id: 'application-method',
        group: 'applicationTracking',
        to: `${DEFINITIONS_BASE_PATH}/application-tracking/application-method`,
        pageKey: 'definitionsApplicationTrackingApplicationMethod',
        labelKey: 'itemLabels.applicationTracking.applicationMethod',
        descriptionKey: 'applicationTracking.applicationMethod',
        icon: ClipboardList,
        color: '#8b5cf6',
    },
    {
        id: 'application-request-information',
        group: 'applicationTracking',
        to: `${DEFINITIONS_BASE_PATH}/application-tracking/application-request-information`,
        pageKey: 'definitionsApplicationTrackingApplicationRequestInformation',
        labelKey: 'itemLabels.applicationTracking.applicationRequestInformation',
        descriptionKey: 'applicationTracking.applicationRequestInformation',
        icon: FileQuestion,
        color: '#f59e0b',
    },
    // Güvenlik
    {
        id: 'security-measure',
        group: 'measures',
        to: `${DEFINITIONS_BASE_PATH}/kvkk/security-measure`,
        pageKey: 'kvkkSecurityMeasure',
        labelKey: 'itemLabels.kvkk.securityMeasure',
        descriptionKey: 'kvkk.securityMeasure',
        icon: Lock,
        color: '#334155',
    },
];

/**
 * @param {import('i18next').TFunction} tNav — definitionsNav
 * @param {(key: string) => boolean} canView
 * @returns {ReferenceNavGroup[]}
 */
export function buildUnifiedPersonalDataNavGroups(tNav, canView) {
    /** @type {Map<string, ReferenceNavItem[]>} */
    const byGroup = new Map();
    for (const entry of PERSONAL_DATA_NAV_CATALOG) {
        if (!canView(entry.pageKey)) continue;
        const item = {
            id: entry.id,
            to: entry.to,
            pageKey: entry.pageKey,
            label: tNav(entry.labelKey),
            description: tNav(entry.descriptionKey, { defaultValue: '' }),
            icon: entry.icon,
            color: entry.color,
            ...(entry.end ? { end: true } : {}),
        };
        if (!byGroup.has(entry.group)) {
            byGroup.set(entry.group, []);
        }
        byGroup.get(entry.group).push(item);
    }
    return PERSONAL_DATA_GROUP_ORDER
        .filter((gid) => byGroup.get(gid)?.length)
        .map((gid) => ({
            id: gid,
            label: tNav(`groupTitles.personalData.${gid}`),
            items: byGroup.get(gid),
        }));
}

const PLATFORM_HUB_ICONS = {
    connectors: Link2,
    'tenant-directory': Network,
    'identity-providers': KeyRound,
    automations: Zap,
    surveys: ClipboardList,
    'survey-responses': ClipboardList,
};

const PLATFORM_HUB_COLORS = {
    connectors: '#6366f1',
    'tenant-directory': '#06b6d4',
    'identity-providers': '#0ea5e9',
    automations: '#f59e0b',
    surveys: '#8b5cf6',
    'survey-responses': '#a855f7',
};

const PLATFORM_HUB_DEFAULT_LABELS = {
    connectors: 'Integration Connectors',
    'tenant-directory': 'Tenant Directory (LDAP)',
    'identity-providers': 'Identity providers',
    automations: 'Automations',
    surveys: 'Surveys',
    'survey-responses': 'Survey Responses',
};

/**
 * @param {import('i18next').TFunction} tNav — definitionsNav
 * @param {(key: string) => boolean} canView
 * @returns {ReferenceNavGroup[]}
 */
export function buildPlatformNavGroups(tNav, canView) {
    const visible = platformHubTabsConfig.tabs.filter((tab) => canView(tab.pageKey));
    if (!visible.length) {
        return [];
    }
    const items = visible.map((tab) => ({
        id: tab.id,
        to: tab.path,
        pageKey: tab.pageKey,
        label: tNav(`platformTabs.${tab.id}`, {
            defaultValue: PLATFORM_HUB_DEFAULT_LABELS[tab.id] || tab.id,
        }),
        description: tNav(`platformNav.${tab.id}`, { defaultValue: '' }),
        icon: PLATFORM_HUB_ICONS[tab.id] || Link2,
        color: PLATFORM_HUB_COLORS[tab.id],
    }));
    return [
        {
            id: 'platformHub',
            label: tNav('groupTitles.platformHub'),
            items,
        },
    ];
}

/**
 * @param {import('i18next').TFunction} _tFallback — kvkkTabs (kept for call-site compatibility)
 * @param {import('i18next').TFunction} tNav — definitionsNav
 * @param {(key: string) => boolean} canView
 * @returns {ReferenceNavGroup[]}
 */
export function buildKvkkNavGroups(_tFallback, tNav, canView) {
    return buildUnifiedPersonalDataNavGroups(tNav, canView);
}

function riskLabelKey(tab) {
    switch (tab.id) {
        case 'bia-impact-scale':
            return 'biaImpactScale';
        case 'risk-assessment-matrix':
            return 'riskAssessmentMatrix';
        case 'cia-matrix':
            return 'ciaMatrix';
        default:
            return tab.id;
    }
}

const RISK_ICONS = {
    'bia-impact-scale': Activity,
    'risk-assessment-matrix': Gem,
    'cia-matrix': Grid3x3,
};

const RISK_COLORS = {
    'bia-impact-scale': '#0891b2',
    'risk-assessment-matrix': '#10b981',
    'cia-matrix': '#f97316',
};

const RISK_TAB_GROUP = {
    'cia-matrix': 'gbv',
    'risk-assessment-matrix': 'scaleAndMatrix',
    'bia-impact-scale': 'scaleAndMatrix',
};

/** Sidebar order: GBE block first, then likelihood–impact scales, then threat/treatment */
const RISK_GROUP_ORDER = ['gbv', 'scaleAndMatrix', 'threatModel', 'treatment'];

/**
 * @param {import('i18next').TFunction} tTabs — matrixConfiguration (tabs.*)
 * @param {import('i18next').TFunction} tNav
 * @param {(key: string) => boolean} canView
 * @returns {ReferenceNavGroup[]}
 */
export function buildRiskAnalysisNavGroups(tTabs, tNav, canView) {
    const visible = riskAnalysisTabsConfig.tabs.filter((tab) => canView(tab.pageKey));
    /** @type {Map<string, ReferenceNavItem[]>} */
    const byGroup = new Map();
    for (const tab of visible) {
        const gid = RISK_TAB_GROUP[tab.id];
        if (!gid) {
            continue;
        }
        const labelKey = riskLabelKey(tab);
        const item = {
            id: tab.id,
            to: tab.path,
            pageKey: tab.pageKey,
            label: tNav(`itemLabels.risk.${labelKey}`, { defaultValue: tTabs(`tabs.${labelKey}`) }),
            description: tNav(`risk.${labelKey}`),
            icon: RISK_ICONS[tab.id] || Gem,
            color: RISK_COLORS[tab.id],
        };
        if (!byGroup.has(gid)) {
            byGroup.set(gid, []);
        }
        byGroup.get(gid).push(item);
    }
    return RISK_GROUP_ORDER.filter((gid) => byGroup.get(gid)?.length)
        .map((gid) => ({
            id: gid,
            label: tNav(`groupTitles.risk.${gid}`),
            items: byGroup.get(gid),
        }));
}

const COMPLIANCE_ICONS = {
    'regulatory-sources': Library,
    clauses: ListTree,
    obligations: ClipboardCheck,
    frameworks: LayoutGrid,
    'report-definitions': BarChart3,
};

const COMPLIANCE_COLORS = {
    'regulatory-sources': '#64748b',
    clauses: '#3b82f6',
    obligations: '#10b981',
    frameworks: '#8b5cf6',
    'report-definitions': '#ec4899',
};

const COMPLIANCE_TAB_GROUP = {
    'regulatory-sources': 'sourcesAndClauses',
    clauses: 'sourcesAndClauses',
    obligations: 'obligationsAndFrameworks',
    frameworks: 'obligationsAndFrameworks',
    'report-definitions': 'obligationsAndFrameworks',
};

const COMPLIANCE_TAB_DEFAULT_LABELS = {
    'regulatory-sources': 'Regulatory Sources',
    clauses: 'Clauses',
    obligations: 'Obligations',
    frameworks: 'Compliance Frameworks',
    'report-definitions': 'Governance Report Definitions',
};

const COMPLIANCE_GROUP_ORDER = ['sourcesAndClauses', 'obligationsAndFrameworks'];

/**
 * @param {import('i18next').TFunction} tNav — definitionsNav
 * @param {(key: string) => boolean} canView
 * @returns {ReferenceNavGroup[]}
 */
export function buildComplianceNavGroups(tNav, canView) {
    const visible = complianceTabsConfig.tabs.filter((tab) => canView(tab.pageKey));
    /** @type {Map<string, ReferenceNavItem[]>} */
    const byGroup = new Map();
    for (const tab of visible) {
        const gid = COMPLIANCE_TAB_GROUP[tab.id];
        if (!gid) {
            continue;
        }
        const item = {
            id: tab.id,
            to: tab.path,
            pageKey: tab.pageKey,
            label: tNav(`complianceTabs.${tab.id}`, {
                defaultValue: COMPLIANCE_TAB_DEFAULT_LABELS[tab.id] || tab.id,
            }),
            description: tNav(`complianceNav.${tab.id}`, { defaultValue: '' }),
            icon: COMPLIANCE_ICONS[tab.id] || Scale,
            color: COMPLIANCE_COLORS[tab.id],
        };
        if (!byGroup.has(gid)) {
            byGroup.set(gid, []);
        }
        byGroup.get(gid).push(item);
    }
    return COMPLIANCE_GROUP_ORDER.filter((gid) => byGroup.get(gid)?.length)
        .map((gid) => ({
            id: gid,
            label: tNav(`groupTitles.compliance.${gid}`),
            items: byGroup.get(gid),
        }));
}

/**
 * @param {import('i18next').TFunction} tNav — definitionsNav
 * @param {(key: string) => boolean} canView
 * @returns {ReferenceNavGroup[]}
 */
export function buildPrivacyMasterNavGroups(tNav, canView) {
    return buildUnifiedPersonalDataNavGroups(tNav, canView);
}

function generalSystemLabelKey(tab) {
    switch (tab.id) {
        case 'units':
            return 'units';
        case 'process':
            return 'process';
        case 'team':
            return 'team';
        case 'duty-definitions':
            return 'dutyDefinition';
        case 'root-cause':
            return 'rootCause';
        case 'mail-template':
            return 'mailTemplate';
        default:
            return tab.id;
    }
}

const GENERAL_ICONS = {
    units: Network,
    process: GitBranch,
    team: Users,
    'duty-definitions': ClipboardList,
    'root-cause': ListChecks,
    'mail-template': Mail,
};

const GENERAL_COLORS = {
    units: '#06b6d4',        // Cyan
    process: '#8b5cf6',      // Violet
    team: '#10b981',         // Emerald
    'duty-definitions': '#0d9488', // Teal
    'root-cause': '#f43f5e', // Rose
    'mail-template': '#f59e0b', // Amber
};

const GENERAL_TAB_GROUP = {
    units: 'organization',
    process: 'organization',
    team: 'organization',
    'duty-definitions': 'organization',
    'root-cause': 'sharedAndComms',
    'mail-template': 'sharedAndComms',
};

const GENERAL_GROUP_ORDER = ['organization', 'sharedAndComms'];

/**
 * @param {import('i18next').TFunction} tBreadcrumb
 * @param {import('i18next').TFunction} tNav
 * @param {(key: string) => boolean} canView
 * @returns {ReferenceNavGroup[]}
 */
export function buildGeneralSystemNavGroups(tBreadcrumb, tNav, canView) {
    const visible = generalSystemTabsConfig.tabs.filter((tab) => canView(tab.pageKey));
    /** @type {Map<string, ReferenceNavItem[]>} */
    const byGroup = new Map();
    for (const tab of visible) {
        const gid = GENERAL_TAB_GROUP[tab.id];
        if (!gid) {
            continue;
        }
        const labelKey = generalSystemLabelKey(tab);
        const item = {
            id: tab.id,
            to: tab.path,
            pageKey: tab.pageKey,
            label: tNav(`itemLabels.generalSystem.${labelKey}`, { defaultValue: tBreadcrumb(labelKey) }),
            description: tNav(`generalSystem.${labelKey}`),
            icon: GENERAL_ICONS[tab.id] || Building2,
            color: GENERAL_COLORS[tab.id],
        };
        if (!byGroup.has(gid)) {
            byGroup.set(gid, []);
        }
        byGroup.get(gid).push(item);
    }
    return GENERAL_GROUP_ORDER.filter((gid) => byGroup.get(gid)?.length)
        .map((gid) => ({
            id: gid,
            label: tNav(`groupTitles.generalSystem.${gid}`),
            items: byGroup.get(gid),
        }));
}

function documentLabelKey(tab) {
    switch (tab.id) {
        case 'document-type':
            return 'documentType';
        case 'classification':
            return 'classification';
        default:
            return tab.id;
    }
}

const DOCUMENT_ICONS = {
    'document-type': FileType,
    classification: Lock,
};

const DOCUMENT_COLORS = {
    'document-type': '#3b82f6',
    classification: '#f43f5e',
};

/**
 * @param {import('i18next').TFunction} tBreadcrumb
 * @param {import('i18next').TFunction} tNav
 * @param {(key: string) => boolean} canView
 * @returns {ReferenceNavGroup[]}
 */
export function buildDocumentNavGroups(tBreadcrumb, tNav, canView) {
    const visible = documentTabsConfig.tabs.filter((tab) => canView(tab.pageKey));
    if (!visible.length) {
        return [];
    }
    const gid = 'classification';
    const items = visible.map((tab) => {
        const labelKey = documentLabelKey(tab);
        return {
            id: tab.id,
            to: tab.path,
            pageKey: tab.pageKey,
            label: tNav(`itemLabels.document.${labelKey}`, { defaultValue: tBreadcrumb(labelKey) }),
            description: tNav(`document.${labelKey}`),
            icon: DOCUMENT_ICONS[tab.id] || FileType,
            color: DOCUMENT_COLORS[tab.id],
        };
    });
    return [
        {
            id: gid,
            label: tNav(`groupTitles.document.${gid}`),
            items,
        },
    ];
}

/**
 * @param {import('i18next').TFunction} _tBreadcrumb
 * @param {import('i18next').TFunction} tNav
 * @param {(key: string) => boolean} canView
 * @returns {ReferenceNavGroup[]}
 */
export function buildApplicationTrackingNavGroups(_tBreadcrumb, tNav, canView) {
    return buildUnifiedPersonalDataNavGroups(tNav, canView);
}

/**
 * @param {import('i18next').TFunction} _tBreadcrumb
 * @param {import('i18next').TFunction} tNav
 * @param {(key: string) => boolean} canView
 * @returns {ReferenceNavGroup[]}
 */
export function buildExplicitConsentNavGroups(_tBreadcrumb, tNav, canView) {
    return buildUnifiedPersonalDataNavGroups(tNav, canView);
}

const ISO_TRAINING_ICONS = {
    list: Library,
    myTraining: GraduationCap,
    competence: Target,
};

const ISO_TRAINING_COLORS = {
    list: '#3b82f6',
    myTraining: '#10b981',
    competence: '#f59e0b',
};

/**
 * @param {import('i18next').TFunction} tTabs — iso27001Training (tabs.*)
 * @param {import('i18next').TFunction} tNav
 * @param {(key: string) => boolean} canView
 * @returns {ReferenceNavGroup[]}
 */
export function buildIsoTrainingNavGroups(tTabs, tNav, canView) {
    const visible = isoTrainingTabsConfig.tabs.filter((tab) => canView(tab.pageKey));
    if (!visible.length) {
        return [];
    }

    const items = visible.map((tab) => ({
        id: tab.id,
        to: tab.path,
        pageKey: tab.pageKey,
        label: tNav(`itemLabels.isoTraining.${tab.id}`, { defaultValue: tTabs(`tabs.${tab.id}`) }),
        description: tNav(`isoTraining.${tab.id}`),
        icon: ISO_TRAINING_ICONS[tab.id] || Library,
        color: ISO_TRAINING_COLORS[tab.id],
    }));

    return [
        {
            id: 'training',
            label: tNav('groupTitles.isoTraining.main'),
            items,
        },
    ];
}

